import { Outlet, useLocation, useNavigate } from 'react-router'
import { SWRConfig } from 'swr'

import { isErrorAPIError } from '@/apiClient/helpers'
import { GET_DATA_ERROR_MESSAGE } from '@/commons/core/shared/constants'
import { useSnackBar } from '@/commons/hooks/useSnackBar'
import { SkipLinksProvider } from '@/components/SkipLinks/SkipLinksContext'
import { SnackBarContainer } from '@/components/SnackBarContainer/SnackBarContainer'
import { BackendVersionMismatch } from '@/pages/Errors/BackendVersionMismatch/BackendVersionMismatch'

import { useBeamer } from './analytics/beamer'
import { useFirebase } from './analytics/firebase'
import { useOrejime } from './analytics/orejime'
import { useSentry } from './analytics/sentry'
import { useCheckBackendVersion } from './hook/useCheckBackendVersion'
import { useFocus } from './hook/useFocus'
import { useLoadFeatureFlags } from './hook/useLoadFeatureFlags'
import { useLogNavigation } from './hook/useLogNavigation'
import { usePageTitle } from './hook/usePageTitle'

window.beamer_config = { product_id: 'vjbiYuMS52566', lazy: true }

export const App = (): JSX.Element | null => {
  const navigate = useNavigate()
  const snackBar = useSnackBar()
  const location = useLocation()
  const backendVersionMismatch = useCheckBackendVersion()

  // Main hooks
  useLoadFeatureFlags()
  usePageTitle()
  useFocus()

  // Analytics
  const { consentedToBeamer, consentedToFirebase } = useOrejime()
  useSentry()
  useBeamer(consentedToBeamer)
  useFirebase(consentedToFirebase)
  useLogNavigation()

  if (backendVersionMismatch !== 'equal') {
    return <BackendVersionMismatch type={backendVersionMismatch} />
  }

  return (
    <>
      <SWRConfig
        value={{
          onError: (error) => {
            if (isErrorAPIError(error) && error.status === 404) {
              if (location.pathname.startsWith('/adage-iframe')) {
                return
              }
              void navigate('/404')
              return
            }
            snackBar.error(GET_DATA_ERROR_MESSAGE)
          },
          revalidateOnFocus: false,
        }}
      >
        <SkipLinksProvider>
          <Outlet />
        </SkipLinksProvider>
      </SWRConfig>
      <SnackBarContainer />
    </>
  )
}
